import {Outlet, useLocation} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {ContentArea} from '@/components/shared/ContentArea'
import {Server} from 'lucide-react'
import {useAuth} from '@/contexts/auth-context'
import {usePageTitle} from '@/hooks/use-page-title'
import {useI18n} from '@/i18n'
import {InlineRow} from '@/components/shared/InlineRow'
import {SidebarNav, type SidebarNavItem} from '@/components/layout/SidebarNav'
import {SidebarNavId} from '@/testids/SidebarNavTestIds'
import {routes} from '@/lib/routes'
import {permissions} from '@/lib/permissions'
import {CONCEPT_ICON, type ConceptKey} from '@/lib/concepts'
import {EmptyState} from '@/components/shared/EmptyState'
import {SectionContainer} from '@/components/shared/SectionContainer'
import {usePromotedTypes, promotedFor, pickPromotedLabel, PROMOTABLE_ENTITY_TABLES, PROMOTED_TESTID_PREFIX, BY_TYPE_PATH_SEGMENT} from '@/hooks/use-promoted-types'
import {PromotedTypeSection, type PromotedTypeView} from '@/types/types'


function buildPromotedHref(entry: PromotedTypeView): string {
    if (entry.entityTable === PROMOTABLE_ENTITY_TABLES.SYSTEM_TYPES) {
        return routes.systems.byType(entry.code)
    }
    return routes.systems.resources.byType(entry.code)
}

function resolveRequiredPermission(entry: PromotedTypeView): string {
    return entry.entityTable === PROMOTABLE_ENTITY_TABLES.SYSTEM_TYPES
        ? permissions.systems.view
        : permissions.resources.view
}

function buildPromotedSidebarItem(
    entry: PromotedTypeView,
    currentLang: string,
    defaultDescription: string,
): SidebarNavItem & { permission: string } {
    const labelText = pickPromotedLabel(entry, currentLang)
    const conceptKey = entry.icon as ConceptKey | undefined
    const fallbackIcon = entry.entityTable === PROMOTABLE_ENTITY_TABLES.SYSTEM_TYPES
        ? CONCEPT_ICON.system
        : CONCEPT_ICON.resource
    const icon = conceptKey && CONCEPT_ICON[conceptKey] ? CONCEPT_ICON[conceptKey] : fallbackIcon
    return {
        id: `${PROMOTED_TESTID_PREFIX}${entry.entityTable}-${entry.code.toLowerCase()}`,
        title: labelText,
        href: buildPromotedHref(entry),
        icon,
        description: defaultDescription,
        permission: resolveRequiredPermission(entry),
    }
}


export function TargetSystemsResources() {
    const i18n = useI18n()
    usePageTitle(i18n.targetSystemsResources.title)
    const location = useLocation()
    const {hasPermission} = useAuth()
    const {i18n: i18nInstance} = useTranslation()
    const lang = i18nInstance.language
    const {promoted} = usePromotedTypes()
    const isRoot = location.pathname === routes.systems.root()

    const menuItems: (SidebarNavItem & { permission: string })[] = [
        {
            id: SidebarNavId.Systems,
            title: i18n.systems.title,
            href: routes.systems.list(),
            icon: CONCEPT_ICON.system,
            description: i18n.systems.menuDescription,
            permission: permissions.systems.view,
            excludePathContains: BY_TYPE_PATH_SEGMENT,
        },
        {
            id: SidebarNavId.Resources,
            title: i18n.resources.title,
            href: routes.systems.resources.list(),
            icon: CONCEPT_ICON.resource,
            description: i18n.resources.menuDescription,
            permission: permissions.resources.view,
            excludePathContains: BY_TYPE_PATH_SEGMENT,
        },
        {
            id: SidebarNavId.EnforcementObjects,
            title: i18n.enforcementObjects.title,
            href: routes.systems.enforcementObjects.list(),
            icon: CONCEPT_ICON.enforcementObject,
            description: i18n.enforcementObjects.menuDescription,
            permission: permissions.enforcementObjects.view,
        },
    ]

    const promotedItems: (SidebarNavItem & { permission: string })[] = promotedFor(promoted, PromotedTypeSection.TARGET_SYSTEMS_RESOURCES)
        .map((entry) => buildPromotedSidebarItem(entry, lang, i18n.systems.menuDescription))

    const visibleMenuItems = [...menuItems, ...promotedItems].filter(item => hasPermission(item.permission))

    return (
        <SectionContainer>
            <InlineRow align="start">
                <SidebarNav items={visibleMenuItems} aria-label={String(i18n.targetSystemsResources.title)}/>

                {/* Content Area */}
                <ContentArea>
                    {isRoot ? (
                        <EmptyState
                            variant="card"
                            icon={Server}
                            title={String(i18n.targetSystemsResources.title)}
                            description={String(i18n.targetSystemsResources.selectOption)}
                            data-testid="target-systems-resources-welcome-card"
                        />
                    ) : (
                        <Outlet/>
                    )}
                </ContentArea>
            </InlineRow>
        </SectionContainer>
    )
}
